// CATALOG-SEARCH-119B — product identity helpers (no second catalog engine).
// Prom product_id must never be labeled as customer-facing SKU.
// CATALOG-SYNC-138-R1 — display dedupe via classifyProductIdentifierDisplay.

import {
  classifyProductIdentifierDisplay,
  type IdentifierDisplayMode,
} from "./identifier-display-v2.js";

export type { IdentifierDisplayMode };
export {
  classifyProductIdentifierDisplay,
  normalizeIdentifierForCompare,
  isValidGtinString,
  resolveManagerIdentifierRows,
} from "./identifier-display-v2.js";

export type BarcodeVerificationState =
  | "source_claimed"
  | "format_valid"
  | "externally_verified"
  | "manually_verified"
  | "conflict"
  | "absent";

export type IdentifierMatchType =
  | "merchant_sku"
  | "merchant_article"
  | "barcode"
  | "gtin"
  | "prom_product_id"
  | "manufacturer_part_number"
  | "text";

/**
 * STAGING-LAUNCH-READINESS-002-R2-R3 — identity-row kinds shown to the customer.
 * `original_sku` (manufacturer part number) is additive and, per the source data
 * today, rare — it is only ever shown when a real, non-empty value exists.
 */
export type CustomerIdentityRowKind = "store_sku" | "original_sku" | "barcode";

export interface ProductIdentityFields {
  promProductId: string;
  merchantSku: string | null;
  merchantArticle: string | null;
  barcodeGtin: string | null;
  barcodeVerification: BarcodeVerificationState;
  manufacturerPartNumber: string | null;
  warehouseCode: string | null;
  sourceSystem: string | null;
  sourceUpdatedAt: string | null;
  lastSyncedAt: string | null;
  sourceTitle: string | null;
  customerTitle: string | null;
  titleAliases: string[];
}

/** Normalize identifier for exact match: trim, strip spaces/dashes, keep leading zeroes. */
export function normalizeIdentifier(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .replace(/[\s\u00A0\-–—]+/g, "")
    .replace(/[\uFF10-\uFF19]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30));
}

export function isEan13FormatValid(code: string): boolean {
  const digits = normalizeIdentifier(code);
  if (!/^\d{13}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    const n = Number(digits[i]);
    sum += i % 2 === 0 ? n : n * 3;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(digits[12]);
}

export function resolveBarcodeVerification(
  code: string | null | undefined,
  claimed: boolean,
): BarcodeVerificationState {
  const norm = normalizeIdentifier(code);
  if (!norm) return "absent";
  if (isEan13FormatValid(norm) || /^\d{8}$/.test(norm) || /^\d{12}$/.test(norm) || /^\d{14}$/.test(norm)) {
    return claimed ? "format_valid" : "format_valid";
  }
  return claimed ? "source_claimed" : "source_claimed";
}

/**
 * Customer-facing primary code for compact chips.
 * CATALOG-SYNC-138-R1: when store_sku equals / is a GTIN, prefer barcode kind.
 */
export function resolveCustomerFacingCode(input: {
  merchantSku?: string | null | undefined;
  merchantArticle?: string | null | undefined;
  barcodeGtin?: string | null | undefined;
  barcodeVerification?: BarcodeVerificationState | null | undefined;
}): { value: string; kind: "merchant_sku" | "merchant_article" | "barcode" } | null {
  const store = String(input.merchantSku ?? "").trim() || String(input.merchantArticle ?? "").trim();
  const c = classifyProductIdentifierDisplay({
    storeSku: store,
    gtin: input.barcodeGtin,
  });
  if (c.customer_display_mode === "barcode_only" && c.display_barcode) {
    return { value: c.display_barcode, kind: "barcode" };
  }
  if (c.display_store_sku) {
    return { value: c.display_store_sku, kind: "merchant_sku" };
  }
  if (c.display_barcode) {
    return { value: c.display_barcode, kind: "barcode" };
  }
  return null;
}

/**
 * STAGING-LAUNCH-READINESS-002-R2-R3 — customer-facing label wording:
 * «Артикул магазину:» for the store's own SKU/article, «Штрихкод:» for a barcode.
 * (See resolveIdentityRows()/identityRowLabel() below for the additional
 * «Оригінальний SKU:» row, which is a separate, rarely-populated field.)
 */
export function customerCodeLabel(
  lang: "ua" | "en",
  kind: "merchant_sku" | "merchant_article" | "barcode",
): string {
  if (lang === "en") {
    if (kind === "barcode") return "Barcode";
    return "Store SKU";
  }
  if (kind === "barcode") return "Штрихкод";
  return "Артикул магазину";
}

/** Row label for the richer multi-row identity block (PDP). Never invented text. */
export function identityRowLabel(lang: "ua" | "en", kind: CustomerIdentityRowKind): string {
  if (lang === "en") {
    if (kind === "barcode") return "Barcode";
    if (kind === "original_sku") return "Manufacturer SKU";
    return "Store SKU";
  }
  if (kind === "barcode") return "Штрихкод";
  if (kind === "original_sku") return "Артикул виробника";
  return "Артикул магазину";
}

/**
 * Full identity row set for richer surfaces (PDP / order detail).
 * CATALOG-SYNC-138-R1: dedupe equal store_sku/gtin → single «Штрихкод» row.
 * Never includes Prom product id. Never renders null/undefined/NaN/AS-hash.
 */
export function resolveIdentityRows(
  lang: "ua" | "en",
  input: {
    merchantSku?: string | null | undefined;
    merchantArticle?: string | null | undefined;
    manufacturerPartNumber?: string | null | undefined;
    originalSkuVerified?: boolean | null | undefined;
    barcodeGtin?: string | null | undefined;
    barcodeVerification?: BarcodeVerificationState | null | undefined;
  },
): Array<{ kind: CustomerIdentityRowKind; label: string; value: string }> {
  const rows: Array<{ kind: CustomerIdentityRowKind; value: string }> = [];
  const storeCandidate =
    String(input.merchantSku ?? "").trim() || String(input.merchantArticle ?? "").trim();
  const c = classifyProductIdentifierDisplay({
    storeSku: storeCandidate,
    gtin: input.barcodeGtin,
  });

  if (c.customer_display_mode === "store_sku_only" || c.customer_display_mode === "store_sku_and_barcode") {
    if (c.display_store_sku) rows.push({ kind: "store_sku", value: c.display_store_sku });
  }
  if (
    c.customer_display_mode === "barcode_only" ||
    c.customer_display_mode === "store_sku_and_barcode"
  ) {
    if (c.display_barcode) rows.push({ kind: "barcode", value: c.display_barcode });
  }

  const originalSku = String(input.manufacturerPartNumber ?? "").trim();
  const storeShown = c.display_store_sku || "";
  const barcodeShown = c.display_barcode || "";
  if (
    input.originalSkuVerified === true &&
    originalSku &&
    originalSku !== "null" &&
    originalSku !== "undefined" &&
    originalSku !== storeShown &&
    originalSku !== barcodeShown &&
    !/^AS[0-9A-Z]{8}$/i.test(originalSku)
  ) {
    rows.push({ kind: "original_sku", value: originalSku });
  }

  return rows.map((row) => ({ ...row, label: identityRowLabel(lang, row.kind) }));
}

/**
 * STAGING-LAUNCH-READINESS-002-R2-R3 — human-readable search match explanation.
 * Exact identifier hits must explain *how* the product was found.
 */
export function searchMatchExplanation(
  lang: "ua" | "en",
  matchedField: string | null | undefined,
): string | null {
  const field = String(matchedField ?? "").trim();
  if (!field) return null;
  if (field === "merchant_sku" || field === "merchant_article" || field === "sku") {
    return lang === "en" ? "Found by store SKU" : "Знайдено за артикулом магазину";
  }
  if (field === "manufacturer_part_number" || field === "original_sku") {
    return lang === "en" ? "Found by original SKU" : "Знайдено за оригінальним SKU";
  }
  if (field === "barcode" || field === "gtin") {
    return lang === "en" ? "Found by barcode" : "Знайдено за штрихкодом";
  }
  if (field === "title" || field === "alias" || field === "brand" || field === "category" || field === "prefix" || field === "fuzzy" || field === "layout" || field === "description") {
    return lang === "en" ? "Found by name" : "Знайдено за назвою";
  }
  // prom_product_id is internal compatibility — do not claim it is a store SKU.
  if (field === "prom_product_id") {
    return lang === "en" ? "Found by internal product id" : "Знайдено за внутрішнім ID товару";
  }
  return null;
}

/** Apply curated customer title; keep source title separate. */
export function resolveDisplayTitle(input: {
  sourceTitle: string;
  customerTitle?: string | null;
}): { display: string; source: string } {
  const source = String(input.sourceTitle ?? "").trim();
  const curated = String(input.customerTitle ?? "").trim();
  return { display: curated || source, source };
}
